import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useNavigationPage from '../../hooks/useNavigationPage';
import useServiceService from '../../app/services/hooks/useServiceService';
import { ServiceDataForm, ServiceFilters } from '../../app/models/Service';
import ServiceDataTable from './components/tables/ServiceDataTable';
import ServiceCreate from './components/ServiceCreate';
import ServiceEdit from './components/ServiceEdit';

const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Servicios', url: '/services', isActive: true }
];

const Services = () => {
    const { navigationPage } = useNavigationPage();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetServices, getServices, deleteService } = useServiceService();

    const [services, setServices] = useState<ServiceDataForm[]>([]);
    const [serviceIdEdit, setServiceIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const confirm = useReactConfirmAlert();

    const [filter, setFilter] = useState<ServiceFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        getServices(filter, {
            onSuccess: (response: ServiceResponse) => {
                const list = Array.isArray(response.data?.services)
                    ? response.data.services
                    : [];
                setServices(list);
                const total = response.data?.total_rows ?? list.length;
                setTotalRows(total);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message ?? 'Error al cargar servicios', { autoClose: 2000 });
                if (response.data?.to_dashboard) navigationPage('/');
                setServices([]);
                setTotalRows(0);
            }
        });
    };

    const showCreate = () => setShowingCreate(true);
    const hideCreate = () => setShowingCreate(false);

    const showEdit = (id: number) => {
        setServiceIdEdit(id);
        setShowingEdit(true);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setServiceIdEdit(-1);
    };

    const destroy = (id: number) => {
        confirm.requestConfirmation({
            title: '¿Estás seguro?',
            message: 'Está a punto de eliminar el servicio #' + id,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando servicio...');
                        deleteService(id, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                confirm.successAlert({ title: 'Éxito', message: response.message });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                confirm.errorAlert({ title: 'Error', message: response.message });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            confirm.infoAlert({ title: 'Cancelado', message: 'El servicio no se ha eliminado.' });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Servicios" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo servicio" />}
                />
                <ServiceDataTable
                    services={services}
                    totalRows={totalRows}
                    loading={fetchingGetServices}
                    edit={showEdit}
                    destroy={destroy}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear servicio"
                    backdrop={true}
                    showFooter={false}
                >
                    <ServiceCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                    />
                </DefaultModal>
            )}

            {showingEdit && serviceIdEdit > 0 && (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar servicio"
                    backdrop={true}
                    showFooter={false}
                >
                    <ServiceEdit
                        serviceId={serviceIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default Services;
